<script setup lang="ts">
import { computed } from 'vue'

/**
 * Native checkbox on the design-system standard (rounded box, primary-emerald check, focus ring).
 * Single-root <input> so id / data-testid / aria-* / extra classes (e.g. alignment `mt-0.5`) fall
 * through. v-model'able; supports `indeterminate` for "select all" partial state. Consolidates the
 * ad-hoc `h-4 w-4 text-blue-600` checkboxes and retires the off-palette blue onto the primary token.
 *
 * The consumer still owns the surrounding <label> / description copy.
 */
interface Props {
  modelValue?: boolean
  indeterminate?: boolean
  disabled?: boolean
  /** Accent: primary (brand emerald, default) · danger (red) · neutral. */
  color?: 'primary' | 'danger'
  /** md (16px, default) · sm (14px, for dense/nested lists). */
  size?: 'sm' | 'md'
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  indeterminate: false,
  disabled: false,
  color: 'primary',
  size: 'md',
})
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const colorClass = computed(
  () =>
    ({
      primary: 'text-primary-600 focus:ring-primary-500',
      danger: 'text-danger-600 focus:ring-danger-500',
    })[props.color],
)
const sizeClass = computed(() => (props.size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'))
</script>

<template>
  <input
    type="checkbox"
    :checked="props.modelValue"
    :indeterminate="props.indeterminate"
    :disabled="props.disabled"
    class="rounded border-gray-300 hover:cursor-pointer focus:ring-2 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
    :class="[colorClass, sizeClass]"
    @change="emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
  />
</template>
